import Phaser from 'phaser';
import type {
  AbilityDisplayTrigger,
  CombatActionResponseDTO,
  CombatLogEntryDTO,
  CombatPosition,
} from '@hexcrawl/shared-utils';
import {
  getMaxHP,
  getMaxMP,
} from '@hexcrawl/shared-utils';
import {
  applyEntityPosition,
  applyEntityVitalsChange,
  appendCombatLogEntry,
  reconcileCombatState,
  useGameStore,
} from '@/ui/stores/gameStore';
import { abilityService } from '@/ui/services/abilityService';
import type { AbilityDisplayEventPayload } from '@/ui/services/abilityDisplayEvents';
import { buildTurnEvents, type EnqueueOptions, type TurnEvent } from '@/ui/services/turnEventQueue';
import { useCombatStore } from '@/ui/stores/combatStore';
import type { CombatCoordinates } from './CombatCoordinates';
import type { CombatAbilityDisplay } from './CombatAbilityDisplay';
import type { CombatContext } from './CombatContext';
import type { CombatEntityManager } from './CombatEntityManager.ts';
import { createCombatMoveTween } from './combatMovement';

export class CombatTurnProcessor {
  private _queueActive = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly coords: CombatCoordinates,
    private readonly abilityDisplay: CombatAbilityDisplay,
    private readonly context: CombatContext,
    private readonly entityManager: CombatEntityManager,
    private readonly onQueueFinished: () => void,
  ) {}

  get queueActive(): boolean {
    return this._queueActive;
  }

  async enqueueAndProcessResponse(response: CombatActionResponseDTO, options: EnqueueOptions = {}) {
    const gameState = useGameStore.getState().gameState;
    const snapshot = gameState?.activeCombat;
    if (!snapshot) return;

    const events = buildTurnEvents(response, snapshot, {
      ...options,
      playerCurrentMP: gameState?.character?.currentMP,
      playerCurrentHP: gameState?.character?.currentHP,
    });
    if (events.length === 0) return;

    this._queueActive = true;
    useCombatStore.getState().setQueueBusy(true);

    try {
      for (const event of events) {
        await this.processQueueEvent(event);
      }
    } finally {
      this._queueActive = false;
      useCombatStore.getState().setQueueBusy(false);
      this.onQueueFinished();
    }
  }

  private async processQueueEvent(event: TurnEvent): Promise<void> {
    switch (event.type) {
      case 'actor_move':
        await this.processActorMove(event.entityId, event.to);
        break;

      case 'add_log_entry':
        appendCombatLogEntry(event.entry);
        break;

      case 'ability_start':
        await this.processAbilityDisplay(event.entry, 'start');
        break;

      case 'apply_delta':
        this.processApplyDelta(event.entityId, event.hpChange, event.mpChange);
        if (event.hpChange !== undefined) await this.delay(200);
        break;

      case 'batch_apply_delta':
        for (const target of event.targets) {
          this.processApplyDelta(target.entityId, target.hpChange, target.mpChange);
        }
        await this.delay(200);
        break;

      case 'ability_complete':
        await this.processAbilityDisplay(event.entry, 'complete');
        break;

      case 'death_sequence':
        await this.entityManager.processDeath(this.scene, event.entityId);
        break;

      case 'combat_end':
        await this.delay(1000);
        useCombatStore.getState().setCombatOutcome(event.result);
        break;

      case 'finalize':
        reconcileCombatState(event.response);
        break;
    }
  }

  private async processActorMove(entityId: string, to: CombatPosition): Promise<void> {
    const sprite = this.entityManager.sprites.get(entityId);
    if (!sprite) {
      applyEntityPosition(entityId, to);
      return;
    }

    const worldPos = this.coords.tileToWorld(to.x + 0.5, to.y + 0.5);
    const dx = Math.abs(sprite.x - worldPos.x);
    const dy = Math.abs(sprite.y - worldPos.y);
    if (dx < 1 && dy < 1) {
      applyEntityPosition(entityId, to);
      return;
    }

    const currentTile = this.coords.worldToTile(sprite.x, sprite.y, true);
    sprite.playAnimation('walk');

    await new Promise<void>((resolve) => {
      const tween = createCombatMoveTween(
        this.scene,
        sprite,
        this.coords,
        this.context.battlefield,
        currentTile,
        to,
        { ignoreEntityId: entityId }
      );
      tween.once(Phaser.Tweens.Events.TWEEN_COMPLETE, () => {
        sprite.playAnimation('idle');
        resolve();
      });
      tween.once(Phaser.Tweens.Events.TWEEN_STOP, () => resolve());
      sprite.sprite.once(Phaser.GameObjects.Events.DESTROY, () => {
        tween.stop();
        resolve();
      });
    });

    applyEntityPosition(entityId, to);
  }

  async runAbilityStartAnimation(
    abilityId: string,
    casterId: string,
    targetId: string | undefined,
    casterPosition: CombatPosition | undefined,
    targetPosition: CombatPosition | undefined,
  ): Promise<void> {
    await this.runAbilityDisplay('start', abilityId, casterId, targetId, casterPosition, targetPosition);
  }

  private async processAbilityDisplay(
    entry: CombatLogEntryDTO,
    trigger: 'start' | 'complete'
  ): Promise<void> {
    if (!entry.abilityUsed) return;

    const casterId = entry.entityId;
    const targets = entry.abilityUsed.targets;
    const firstTargetId = targets.length > 0 ? targets[0].targetId : undefined;
    const combat = this.context.combat;

    const casterState = combat.entityStates.find(e => e.entityId === casterId);
    const targetState = firstTargetId
      ? combat.entityStates.find(e => e.entityId === firstTargetId)
      : undefined;

    // Prefer targetPosition from log entry (AoE blast center) over entity position
    const targetPos = entry.abilityUsed.targetPosition ?? targetState?.position;

    const args: [string, string, string | undefined, CombatPosition | undefined, CombatPosition | undefined] = [
      entry.abilityUsed.abilityId,
      casterId,
      firstTargetId,
      casterState?.position,
      targetPos,
    ];

    if (trigger === 'complete') {
      const totalDamage = targets.reduce((sum, t) => sum + t.damage, 0);
      const outcomeTrigger: AbilityDisplayTrigger = totalDamage > 0 ? 'damage' : 'miss';
      await Promise.all([
        this.runAbilityDisplay(outcomeTrigger, ...args),
        this.runAbilityDisplay('complete', ...args),
      ]);
    } else {
      await this.runAbilityDisplay(trigger, ...args);
    }
  }

  private async runAbilityDisplay(
    trigger: AbilityDisplayTrigger,
    abilityId: string,
    casterId: string,
    targetId: string | undefined,
    casterPosition: CombatPosition | undefined,
    targetPosition: CombatPosition | undefined,
  ): Promise<void> {
    if (casterPosition && targetPosition) {
      const casterSprite = this.entityManager.sprites.get(casterId);
      if (casterSprite && (casterPosition.x !== targetPosition.x || casterPosition.y !== targetPosition.y)) {
        casterSprite.setFacing(this.coords.getFacingBetweenPositions(casterPosition, targetPosition));
      }
    }

    let ability = null;
    try {
      ability = abilityService.getAbilityFromCache(abilityId)
        ?? await abilityService.getAbility(abilityId);
    } catch {
      // Ability not found; skip display
    }
    if (!ability?.displayEvents?.length) return;

    const payload: AbilityDisplayEventPayload = {
      trigger,
      abilityId,
      casterId,
      targetId,
      casterPosition,
      targetPosition,
    };

    const animPromises: Promise<void>[] = [];
    for (const displayEvent of ability.displayEvents) {
      if (displayEvent.trigger !== trigger) continue;
      for (const action of displayEvent.actions) {
        const p = this.abilityDisplay.executeAbilityDisplayActionAsync(action, payload);
        if (p) animPromises.push(p);
      }
    }

    if (animPromises.length > 0) {
      await Promise.all(animPromises);
    }
  }

  private processApplyDelta(
    entityId: string,
    hpChange?: number,
    mpChange?: number
  ) {
    applyEntityVitalsChange(entityId, hpChange ?? 0, mpChange ?? 0);

    const sprite = this.entityManager.sprites.get(entityId);
    const entity = this.context.roster[entityId];
    if (!sprite || !entity) return;

    // Re-read vitals from the just-updated store
    this.context.updateFromGameStore();
    const combat = this.context.combat;
    const entityState = combat.entityStates.find(e => e.entityId === entityId);
    if (!entityState) return;

    const maxHP = getMaxHP(entity);
    const maxMP = getMaxMP(entity);
    const { currentHP, currentMP } = this.context.getEntityVitals(entityState, maxHP, maxMP);
    sprite.setVitals(currentHP, currentMP);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      if (this.scene.scene.isActive()) {
        this.scene.time.delayedCall(ms, resolve);
      } else {
        resolve();
      }
    });
  }
}
